import type { StateStore } from 'oidc-client-ts';

const PREFIX = 'wc.oidc.';

function isAvailable(): boolean {
    return (
        typeof __IN_EXTENSION__ !== 'undefined' &&
        __IN_EXTENSION__ &&
        typeof chrome !== 'undefined' &&
        !!chrome?.storage?.session
    );
}

function getStorageKey(key: string): string {
    return PREFIX + key;
}

/**
 * StateStore backed by chrome.storage.session so OIDC state is shared
 * between the side panel (where we may build the sign-in URL) and the
 * tab that handles the callback after Cognito redirects.
 */
export function createChromeStorageStateStore(): StateStore {
    return {
        async get(key: string): Promise<string | null> {
            if (!isAvailable()) return null;
            const sk = getStorageKey(key);
            return new Promise(resolve => {
                chrome!.storage!.session.get([sk], result => {
                    resolve((result[sk] as string) ?? null);
                });
            });
        },
        async set(key: string, value: string): Promise<void> {
            if (!isAvailable()) return;
            return new Promise(resolve => {
                chrome!.storage!.session.set({ [getStorageKey(key)]: value }, () => resolve());
            });
        },
        async remove(key: string): Promise<string | null> {
            if (!isAvailable()) return null;
            const sk = getStorageKey(key);
            const existing = await new Promise<string | null>(res => {
                chrome!.storage!.session.get([sk], r => res((r[sk] as string) ?? null));
            });
            return new Promise(resolve => {
                chrome!.storage!.session.remove(sk, () => resolve(existing));
            });
        },
        async getAllKeys(): Promise<string[]> {
            if (!isAvailable()) return [];
            return new Promise(resolve => {
                // get(null) returns all keys (Chrome API); type defs may require assertion
                chrome!.storage!.session.get(null as unknown as string[], items => {
                    const keys = Object.keys(items || {})
                        .filter(k => k.startsWith(PREFIX))
                        .map(k => k.slice(PREFIX.length));
                    resolve(keys);
                });
            });
        },
    };
}
